import React, { useEffect, useState } from 'react';

import PageLayout from 'layouts/PageLayout';
import PanelContainer from 'components/PanelContainer';
import Purchase from 'components/Purchase';
import BasicButton from 'components/BasicButton';
import ResponseErrorDialog from 'scenes/Dialog/components/ResponseErrorDialog';
import RecruiterSubscriptionRequestDialog from 'scenes/Dialog/components/RecruiterSubscriptionRequestDialog';
import FeaturesTable from 'components/FeaturesTable';
import FeaturesTableRow from 'components/FeaturesTable/components/FeaturesTableRow';
import FeaturesTableColumn from 'components/FeaturesTable/components/FeaturesTableColumn';
import ROUTE from 'constants/route';
import LoadMask from 'components/LoadMask';

import { ENDPOINT } from 'constants/endpoints';
import { useApi } from 'hooks/useApi';

import { CSSTransition } from 'react-transition-group';
import { ANIMATION_TIMEOUT_APPEAR } from 'constants/animation.js';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';

import './styles.css';

const PurchaseRecruiter = props => {
  const {
    history,
    location: { state = { returnUrl: null } },
  } = props;

  const [price, setPrice] = useState({
    basic: '',
    premium: '',
  });

  const [isFetching, setFetching] = useState(true);

  const api = useApi();

  useEffect(() => {
    api.get(
      ENDPOINT.OTHER.PRICING_RECRUITER_CREDITS,
      {},
      {
        handleNotFound: () => history.replace(ROUTE.ERROR_404),
        handleForbidden: () => history.replace(ROUTE.ERROR_404),
        handleError: response => ResponseErrorDialog({ response }),
        handleSuccess: price => setPrice(price),
        handleComplete: () => setFetching(false),
      }
    );
  }, []);

  const handleStartBasic = () => {
    history.push(ROUTE.CHECKOUT_CREDIT, { returnUrl: state.returnUrl });
  };

  const handleStartPremium = () => {
    history.push(ROUTE.CHECKOUT_CREDITS, { returnUrl: state.returnUrl });
  };

  const handleStartPremiumPrep = () =>
    RecruiterSubscriptionRequestDialog({
      showTitle: false,
      initialText: 'Would you like to send request to learn more?',
    });

  return (
    <PageLayout
      pageClass="purchase"
      pageTitle="Compare Plans"
      mainContent={
        <CSSTransition
          in={true}
          appear={true}
          timeout={ANIMATION_TIMEOUT_APPEAR}
          classNames="fade"
        >
          <PanelContainer
            styles={{
              marginBottom: '10px',
              paddingTop: '35px',
              position: 'relative',
            }}
          >
            <Purchase>
              {isFetching && <LoadMask relative />}
              <div className="purchase__header">
                <div className="purchase__block">
                  <h3 className="purchase__title">Compare Plans</h3>
                  <div className="purchase__pricing">
                    Pricing:
                    <span className="purchase__accent">Per Package</span>
                  </div>
                </div>

                <div className="purchase__item">
                  <h4 className="purchase__heading">Basic</h4>
                  <div className="purchase__price">
                    <div className="purchase__value">
                      ${price.basic || '---'}
                    </div>
                  </div>
                  <div className="purchase__description">1 Job Credit</div>

                  <div className="purchase__action">
                    <BasicButton
                      disabled={!price.basic}
                      btnWidth={140}
                      btnHeight={34}
                      btnFontWeight={600}
                      btnText="Start Now"
                      btnArrow={false}
                      btnBg="#fff"
                      btnHoverBg="#f40"
                      controlFunc={handleStartBasic}
                    />
                  </div>
                </div>

                <div className="purchase__item">
                  <h4 className="purchase__heading">Premium</h4>
                  <div className="purchase__price">
                    <div className="purchase__value">
                      ${price.premium || '---'}
                    </div>
                  </div>
                  <div className="purchase__description">3 Job Credits</div>

                  <div className="purchase__action">
                    <BasicButton
                      disabled={!price.premium}
                      btnWidth={140}
                      btnHeight={34}
                      btnFontWeight={600}
                      btnText="Start Now"
                      btnArrow={false}
                      btnBg="#fff"
                      btnHoverBg="#f40"
                      controlFunc={handleStartPremium}
                    />
                  </div>
                </div>

                <div className="purchase__item purchase__item--accent">
                  <h4 className="purchase__heading purchase__heading--accent">
                    Premium <span className="purchase__accent">+ Search</span>
                  </h4>
                  <div className="purchase__price">
                    <div className="purchase__value">Subscription</div>
                  </div>
                  <div className="purchase__description">Billed annually</div>

                  <div className="purchase__action">
                    <BasicButton
                      btnWidth={140}
                      btnHeight={34}
                      btnFontWeight={600}
                      btnText="Learn More"
                      btnArrow={false}
                      btnBg="#fff"
                      btnHoverBg="#f40"
                      controlFunc={handleStartPremiumPrep}
                    />
                  </div>
                </div>
              </div>

              <div className="purchase__content">
                <FeaturesTable>
                  <FeaturesTableRow>
                    <FeaturesTableColumn
                      title="Job Credits"
                      description="60 day targeted job posting"
                    />

                    <FeaturesTableColumn name="Basic" content={1} />

                    <FeaturesTableColumn name="Premium" content={3} />

                    <FeaturesTableColumn
                      name="Premium + Search"
                      content="
                      Multiple Packages
                    "
                    />
                  </FeaturesTableRow>

                  <FeaturesTableRow>
                    <FeaturesTableColumn
                      title="Manage Process"
                      description="Create resume books and applicant spreadsheets"
                    />

                    <FeaturesTableColumn
                      name="Basic"
                      content={
                        <FontAwesomeIcon
                          icon={faCheck}
                          color="#27282d"
                          size="lg"
                        />
                      }
                    />

                    <FeaturesTableColumn
                      name="Premium"
                      content={
                        <FontAwesomeIcon
                          icon={faCheck}
                          color="#27282d"
                          size="lg"
                        />
                      }
                    />

                    <FeaturesTableColumn
                      name="Premium + Search"
                      content={
                        <FontAwesomeIcon
                          icon={faCheck}
                          color="#27282d"
                          size="lg"
                        />
                      }
                    />
                  </FeaturesTableRow>

                  <FeaturesTableRow>
                    <FeaturesTableColumn
                      title="Job Statistics"
                      description="See applicant statistics on all jobs"
                    />

                    <FeaturesTableColumn
                      name="Basic"
                      content={
                        <FontAwesomeIcon
                          icon={faCheck}
                          color="#27282d"
                          size="lg"
                        />
                      }
                    />

                    <FeaturesTableColumn
                      name="Premium"
                      content={
                        <FontAwesomeIcon
                          icon={faCheck}
                          color="#27282d"
                          size="lg"
                        />
                      }
                    />

                    <FeaturesTableColumn
                      name="Premium + Search"
                      content={
                        <FontAwesomeIcon
                          icon={faCheck}
                          color="#27282d"
                          size="lg"
                        />
                      }
                    />
                  </FeaturesTableRow>

                  <FeaturesTableRow>
                    <FeaturesTableColumn
                      title="GoBuyside Aptitude Test"
                      description="Administer our finance test to applicants"
                    />

                    <FeaturesTableColumn
                      name="Basic"
                      content={
                        <FontAwesomeIcon
                          icon={faCheck}
                          color="#27282d"
                          size="lg"
                        />
                      }
                    />

                    <FeaturesTableColumn
                      name="Premium"
                      content={
                        <FontAwesomeIcon
                          icon={faCheck}
                          color="#27282d"
                          size="lg"
                        />
                      }
                    />

                    <FeaturesTableColumn
                      name="Premium + Search"
                      content={
                        <FontAwesomeIcon
                          icon={faCheck}
                          color="#27282d"
                          size="lg"
                        />
                      }
                    />
                  </FeaturesTableRow>

                  <FeaturesTableRow>
                    <FeaturesTableColumn
                      title="Firm Reviews"
                      description="Access to firm compensation reviews"
                    />

                    <FeaturesTableColumn
                      name="Basic"
                      content={
                        <FontAwesomeIcon
                          icon={faCheck}
                          color="#27282d"
                          size="lg"
                        />
                      }
                    />

                    <FeaturesTableColumn
                      name="Premium"
                      content={
                        <FontAwesomeIcon
                          icon={faCheck}
                          color="#27282d"
                          size="lg"
                        />
                      }
                    />

                    <FeaturesTableColumn
                      name="Premium + Search"
                      content={
                        <FontAwesomeIcon
                          icon={faCheck}
                          color="#27282d"
                          size="lg"
                        />
                      }
                    />
                  </FeaturesTableRow>

                  <FeaturesTableRow>
                    <FeaturesTableColumn
                      title="Search Functionality"
                      description="Search and message passive candidates"
                    />

                    <FeaturesTableColumn
                      name="Basic"
                      content={
                        <FontAwesomeIcon
                          icon={faTimes}
                          color="#f40"
                          size="lg"
                        />
                      }
                    />

                    <FeaturesTableColumn
                      name="Premium"
                      content={
                        <FontAwesomeIcon
                          icon={faTimes}
                          color="#f40"
                          size="lg"
                        />
                      }
                    />

                    <FeaturesTableColumn
                      name="Premium + Prep"
                      content={
                        <FontAwesomeIcon
                          icon={faCheck}
                          color="#27282d"
                          size="lg"
                        />
                      }
                    />
                  </FeaturesTableRow>
                </FeaturesTable>
              </div>
            </Purchase>
          </PanelContainer>
        </CSSTransition>
      }
    />
  );
};

export default PurchaseRecruiter;
